/**
 * Feature Catalog — frontend mirror of backend/src/config/features.ts.
 *
 * Kept in lockstep with the backend by convention (a unit test asserts parity).
 * The frontend uses this to render <FeatureGate> wrappers and to drive the
 * Dev Console feature-flag admin UI. The backend remains the security
 * boundary — gated API endpoints enforce visibility via feature-gate middleware
 * regardless of what the frontend does.
 *
 * Stage semantics (before per-org overrides):
 *   - mvp:               visible to every authenticated user
 *   - base:              visible to @neeve.ai employees (unless previewAsConsumer)
 *   - deep:              visible to @neeve.ai employees (unless previewAsConsumer)
 *   - scope='dev-console': always visible to role === 'dev'
 */

export type FeatureStage = 'mvp' | 'base' | 'deep';
export type FeatureScope = 'route' | 'widget' | 'capability' | 'dev-console';

export interface FeatureDefinition {
  id: string;
  stage: FeatureStage;
  scope: FeatureScope;
  label: string;
  description?: string;
}

export const FEATURE_CATALOG: FeatureDefinition[] = [
  // ── Stage: MVP — visible to all authenticated users ────────────────────────
  { id: 'route.analytics.overview', stage: 'mvp', scope: 'route', label: 'Analytics — Overview' },
  { id: 'route.analytics.active-users', stage: 'mvp', scope: 'route', label: 'Analytics — Active Users (Remote Access)' },
  { id: 'route.analytics.active-endpoints', stage: 'mvp', scope: 'route', label: 'Analytics — Active Endpoint Connections' },
  { id: 'route.analytics.security-policies', stage: 'mvp', scope: 'route', label: 'Analytics — Security Policies' },
  { id: 'route.settings', stage: 'mvp', scope: 'route', label: 'Settings' },

  // ── Stage: BASE — current Insights pages (hidden from consumers in MVP) ────
  { id: 'route.dashboard', stage: 'base', scope: 'route', label: 'Secure Edge — Dashboard' },
  { id: 'route.triage', stage: 'base', scope: 'route', label: 'Secure Edge — Triage' },
  { id: 'route.portfolios', stage: 'base', scope: 'route', label: 'Secure Edge — Portfolios' },
  { id: 'route.fleet', stage: 'base', scope: 'route', label: 'Secure Edge — Inventory' },
  { id: 'route.nodes', stage: 'base', scope: 'route', label: 'Secure Edge — Nodes' },
  { id: 'route.networks', stage: 'base', scope: 'route', label: 'Secure Edge — Networks' },
  { id: 'route.services', stage: 'base', scope: 'route', label: 'Secure Edge — Services' },
  { id: 'route.security', stage: 'base', scope: 'route', label: 'Secure Edge — Security' },
  { id: 'route.users', stage: 'base', scope: 'route', label: 'Secure Edge — Users' },

  // ── Stage: DEEP — paywalled (Deep Insights tier) ───────────────────────────
  { id: 'widget.chat.llm', stage: 'deep', scope: 'widget', label: 'LLM Chat Bubble' },
  { id: 'capability.smart-alerting', stage: 'deep', scope: 'capability', label: 'Smart Alerting & Workflows' },
  { id: 'capability.cmms-integration', stage: 'deep', scope: 'capability', label: 'CMMS Integration' },
  { id: 'capability.agentic-ai', stage: 'deep', scope: 'capability', label: 'Agentic AI' },
  { id: 'capability.api-integrations', stage: 'base', scope: 'capability', label: 'API & Webhooks (Self-Service)' },

  // ── Scope: DEV-CONSOLE — always visible to role === 'dev' users ────────────
  { id: 'route.dev-console', stage: 'base', scope: 'dev-console', label: 'Dev Console' },
];

export const FEATURE_BY_ID: Record<string, FeatureDefinition> = Object.fromEntries(
  FEATURE_CATALOG.map((f) => [f.id, f])
);

export function getFeature(id: string): FeatureDefinition | undefined {
  return FEATURE_BY_ID[id];
}
